'use client';

import { c as _c } from "react/compiler-runtime";
import React, { Suspense } from 'react';
import type { SvgIconProps, SvgIconTypeMap } from '@mui/material/SvgIcon';
import SvgIcon from '@mui/material/SvgIcon';
import dynamic from 'next/dynamic';
import { classNames } from '@/lib/classNames';
const iconMap = {
  addcard: dynamic(() => import('@/components/JucyIcon/svg/addcard.svg')),
  arrow_dotted_down: dynamic(() => import('@/components/JucyIcon/svg/arrow_dotted_down.svg')),
  babyseat: dynamic(() => import('@/components/JucyIcon/svg/babyseat.svg')),
  battery: dynamic(() => import('@/components/JucyIcon/svg/battery.svg')),
  bed: dynamic(() => import('@/components/JucyIcon/svg/bed.svg')),
  boosterseat: dynamic(() => import('@/components/JucyIcon/svg/boosterseat.svg')),
  calendar: dynamic(() => import('@/components/JucyIcon/svg/calendar.svg')),
  campa: dynamic(() => import('@/components/JucyIcon/svg/campa.svg')),
  campervan: dynamic(() => import('@/components/JucyIcon/svg/campervan.svg')),
  campingchair: dynamic(() => import('@/components/JucyIcon/svg/campingchair.svg')),
  campingtable: dynamic(() => import('@/components/JucyIcon/svg/campingtable.svg')),
  car: dynamic(() => import('@/components/JucyIcon/svg/car.svg')),
  cardoneoff: dynamic(() => import('@/components/JucyIcon/svg/cardoneoff.svg')),
  cardonfile: dynamic(() => import('@/components/JucyIcon/svg/cardonfile.svg')),
  chanceit: dynamic(() => import('@/components/JucyIcon/svg/chanceit.svg')),
  clock: dynamic(() => import('@/components/JucyIcon/svg/clock.svg')),
  coach: dynamic(() => import('@/components/JucyIcon/svg/coach.svg')),
  coffee: dynamic(() => import('@/components/JucyIcon/svg/coffee.svg')),
  cruise: dynamic(() => import('@/components/JucyIcon/svg/cruise.svg')),
  depositpayment: dynamic(() => import('@/components/JucyIcon/svg/depositpayment.svg')),
  dps: dynamic(() => import('@/components/JucyIcon/svg/dps.svg')),
  electric_socket: dynamic(() => import('@/components/JucyIcon/svg/electric_socket.svg')),
  engine: dynamic(() => import('@/components/JucyIcon/svg/engine.svg')),
  fan: dynamic(() => import('@/components/JucyIcon/svg/fan.svg')),
  fullpayment: dynamic(() => import('@/components/JucyIcon/svg/fullpayment.svg')),
  gps: dynamic(() => import('@/components/JucyIcon/svg/gps.svg')),
  hour_reception: dynamic(() => import('@/components/JucyIcon/svg/hour_reception.svg')),
  images: dynamic(() => import('@/components/JucyIcon/svg/images.svg')),
  kitchen_facilities: dynamic(() => import('@/components/JucyIcon/svg/kitchen_facilities.svg')),
  laundry: dynamic(() => import('@/components/JucyIcon/svg/laundry.svg')),
  locker: dynamic(() => import('@/components/JucyIcon/svg/locker.svg')),
  logo_yelp: dynamic(() => import('@/components/JucyIcon/svg/logo_yelp.svg')),
  lounge: dynamic(() => import('@/components/JucyIcon/svg/lounge.svg')),
  luggage: dynamic(() => import('@/components/JucyIcon/svg/luggage.svg')),
  manualpayment: dynamic(() => import('@/components/JucyIcon/svg/manualpayment.svg')),
  motorhome: dynamic(() => import('@/components/JucyIcon/svg/motorhome.svg')),
  partialpayment: dynamic(() => import('@/components/JucyIcon/svg/partialpayment.svg')),
  plane: dynamic(() => import('@/components/JucyIcon/svg/plane.svg')),
  privacy_blind: dynamic(() => import('@/components/JucyIcon/svg/privacy_blind.svg')),
  refund: dynamic(() => import('@/components/JucyIcon/svg/refund.svg')),
  risktaker: dynamic(() => import('@/components/JucyIcon/svg/risktaker.svg')),
  'roof-racks': dynamic(() => import('@/components/JucyIcon/svg/roof-racks.svg')),
  seats: dynamic(() => import('@/components/JucyIcon/svg/seats.svg')),
  secure_parking: dynamic(() => import('@/components/JucyIcon/svg/secure_parking.svg')),
  'self-contained': dynamic(() => import('@/components/JucyIcon/svg/self-contained.svg')),
  sendlink: dynamic(() => import('@/components/JucyIcon/svg/sendlink.svg')),
  shower: dynamic(() => import('@/components/JucyIcon/svg/shower.svg')),
  skootwifi: dynamic(() => import('@/components/JucyIcon/svg/skootwifi.svg')),
  sleeps: dynamic(() => import('@/components/JucyIcon/svg/sleeps.svg')),
  smartphone: dynamic(() => import('@/components/JucyIcon/svg/smartphone.svg')),
  snooze: dynamic(() => import('@/components/JucyIcon/svg/snooze.svg')),
  'snooze-2v': dynamic(() => import('@/components/JucyIcon/svg/snooze-2v.svg')),
  'snooze_48px-3v': dynamic(() => import('@/components/JucyIcon/svg/snooze_48px-3v.svg')),
  snowchains: dynamic(() => import('@/components/JucyIcon/svg/snowchains.svg')),
  storage_net: dynamic(() => import('@/components/JucyIcon/svg/storage_net.svg')),
  stressfree: dynamic(() => import('@/components/JucyIcon/svg/stressfree.svg')),
  terminal: dynamic(() => import('@/components/JucyIcon/svg/terminal.svg')),
  transmission: dynamic(() => import('@/components/JucyIcon/svg/transmission.svg')),
  travel: dynamic(() => import('@/components/JucyIcon/svg/travel.svg')),
  tv: dynamic(() => import('@/components/JucyIcon/svg/tv.svg')),
  usb_charging: dynamic(() => import('@/components/JucyIcon/svg/usb_charging.svg')),
  walk: dynamic(() => import('@/components/JucyIcon/svg/walk.svg')),
  wifi: dynamic(() => import('@/components/JucyIcon/svg/wifi.svg'))
} as const;
export function getAllIcons(): JucyIcon[] {
  return Object.keys(iconMap) as JucyIcon[];
}
export type JucyIcon = keyof typeof iconMap;
export type JucyIconProps<RootComponent extends React.ElementType = SvgIconTypeMap['defaultComponent'], AdditionalProps = object> = Omit<SvgIconProps<RootComponent, AdditionalProps>, 'viewBox'> & {
  icon: JucyIcon;
  className?: string;
};
export function JucyIcon(t0) {
  const $ = _c(9);
  let className;
  let icon;
  let props;
  if ($[0] !== t0) {
    ({
      icon,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = icon;
    $[3] = props;
  } else {
    className = $[1];
    icon = $[2];
    props = $[3];
  }
  const IconComponent = iconMap[icon];
  let t1;
  if ($[4] !== IconComponent || $[5] !== className || $[6] !== icon || $[7] !== props) {
    t1 = IconComponent ? <Suspense><SvgIcon className={classNames("jucy-icon", icon, className)} viewBox="0 0 32 32" {...props}><IconComponent /></SvgIcon></Suspense> : null;
    $[4] = IconComponent;
    $[5] = className;
    $[6] = icon;
    $[7] = props;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}